import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Ban, CheckCircle2, Download, FileText, History, Lock, Pencil,
  Phone, Play, Save, ShieldAlert, Upload,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import CandidateProfileFields from "@/components/candidate/CandidateProfileFields";

const ANY = "__any__";

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const ReadValue = ({ value }) => (
  <p className="text-sm font-medium min-h-[20px]">{Array.isArray(value) ? (value.length ? value.join(", ") : "—") : (value ?? "—") || "—"}</p>
);

export default function CandidateProfile() {
  const { candidateId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const backPath = user.role === "recruiter" ? "/recruiter/candidates" : "/admin/candidates";

  const [cand, setCand] = useState(null);
  const [master, setMaster] = useState({});
  const [journey, setJourney] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [disposition, setDisposition] = useState("");
  const [closeNote, setCloseNote] = useState("");
  // M17: Directory claim → fresh application
  const [claimOpen, setClaimOpen] = useState(false);
  const [conflict, setConflict] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, m, j, r] = await Promise.all([
        api.get(`/candidates/${candidateId}`),
        api.get("/master-data"),
        api.get(`/candidates/${candidateId}/journey`),
        api.get(`/candidates/${candidateId}/resumes`),
      ]);
      setCand(c.data);
      setForm(c.data);
      setMaster(m.data);
      setJourney(j.data);
      setResumes(r.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const canEdit = cand?.can_edit && (editing || false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const fields = {};
      ["full_name", "mobile", "alt_mobile", "email", "dob", "gender", "current_address",
        "current_city", "current_state", "preferred_work_locations",
        "employment_status", "current_company", "current_designation", "industry", "department",
        "job_role", "total_experience", "current_salary", "expected_salary", "notice_period", "available_to_join",
        "highest_qualification", "course", "specialization", "passing_year",
        "languages", "skills", "certifications",
        "preferred_job_roles", "preferred_locations", "preferred_salary", "source",
      ].forEach((k) => { fields[k] = form[k] ?? null; });
      if (fields.current_salary) fields.current_salary = Number(fields.current_salary);
      if (fields.expected_salary) fields.expected_salary = Number(fields.expected_salary);
      if (fields.preferred_salary) fields.preferred_salary = Number(fields.preferred_salary);
      const res = await api.put(`/candidates/${candidateId}`, { fields });
      setCand(res.data);
      setForm(res.data);
      setEditing(false);
      toast.success("Profile saved");
      api.get(`/candidates/${candidateId}/journey`).then((j) => setJourney(j.data));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === "object") {
        const ownerLine = detail.owner_name
          ? `Currently with ${detail.owner_name}${detail.owner_mobile ? ` · ${detail.owner_mobile}` : ""}`
          : "Currently unassigned (in the pool)";
        toast.error(detail.message, { description: ownerLine, duration: 8000 });
      } else {
        toast.error(typeof detail === "object" ? detail.message : getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const openClaim = () => {
    setClaimOpen(true);
  };

  const submitClaim = async () => {
    setBusy(true);
    try {
      // Job is auto-assigned by the backend (candidate's last job → latest active
      // job → hidden placeholder) — recruiters never pick one.
      const res = await api.post(`/recruiter-flow/candidates/${candidateId}/claim-to-application`, {});
      toast.success(`You are now working on ${cand.full_name} — fresh application ${res.data.app_code} created`);
      setClaimOpen(false);
      navigate(`/recruiter/applications/${res.data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "ACTIVE_CANDIDATE_EXISTS") {
        setClaimOpen(false);
        setConflict(detail);
      } else if (detail && typeof detail === "object" && detail.existing_id) {
        toast.error(detail.message || "An active application already exists for this candidate and job");
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const closeLead = async () => {
    if (!disposition) { toast.error("Select a disposition"); return; }
    setBusy(true);
    try {
      await api.post(`/candidates/${candidateId}/close`, { disposition, note: closeNote || null });
      toast.success(`Lead closed — ${disposition}`);
      setCloseOpen(false);
      setDisposition("");
      setCloseNote("");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async (payload) => {
    try {
      const res = await api.put(`/candidates/${candidateId}/notes`, payload);
      setCand(res.data);
      setForm(res.data);
      toast.success("Saved");
      api.get(`/candidates/${candidateId}/journey`).then((j) => setJourney(j.data));
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const uploadResume = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Resume must be 5 MB or smaller"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/candidates/${candidateId}/resumes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded — marked as Active version");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadResume = async (r) => {
    try {
      const res = await api.get(`/candidates/${candidateId}/resumes/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-24 rounded-xl" /><Skeleton className="h-96 rounded-xl" /></div>;
  }
  if (!cand) return <p className="text-sm text-muted-foreground">Candidate not found.</p>;

  const ownedByOther = cand.assigned_recruiter_id && cand.assigned_recruiter_id !== user.id && !isAdmin;
  const isOwner = cand.assigned_recruiter_id === user.id;

  return (
    <div className="space-y-5" data-testid="candidate-profile-page">
      <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="candidate-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to candidates
      </Link>

      {/* Header */}
      <Card className="rounded-xl border-border shadow-none">
        <CardContent className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <span className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center text-lg font-semibold shrink-0">
                {(cand.full_name || "?").charAt(0).toUpperCase()}
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-heading font-semibold" data-testid="candidate-profile-name">{cand.full_name || "Unnamed"}</h1>
                  <Badge variant="outline" className="rounded-full font-mono text-[10px]" data-testid="candidate-profile-code">{cand.code}</Badge>
                  {cand.blacklisted && <Badge className="rounded-full text-[10px] bg-red-600 text-white" data-testid="candidate-blacklisted-badge"><Ban className="h-3 w-3 mr-1" />Blacklisted</Badge>}
                  {cand.do_not_contact && <Badge variant="outline" className="rounded-full text-[10px] bg-amber-50 text-amber-700 border-amber-200" data-testid="candidate-dnc-badge">Do Not Contact</Badge>}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                  <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /><span className="font-mono">{cand.mobile}</span>
                    {cand.mobile_masked && <Badge variant="outline" className="rounded-full text-[10px] ml-1 bg-slate-50 text-slate-600 border-slate-200" data-testid="candidate-mobile-masked-badge">Masked until claimed</Badge>}
                  </span>
                  {cand.email && <span>{cand.email}</span>}
                  {cand.current_city && <span>{cand.current_city}</span>}
                  <span>Source: {cand.source || "—"}</span>
                </p>
                <p className="text-xs mt-1" data-testid="candidate-owner-info">
                  {cand.assigned_recruiter_id
                    ? <span className="text-primary font-medium">{isOwner ? "You are working on this candidate" : `Being worked on by ${cand.owner_name}`}</span>
                    : <span className="text-muted-foreground">No recruiter is working on this candidate{cand.last_disposition ? ` · Last disposition: ${cand.last_disposition}` : ""}</span>}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-stretch sm:items-end gap-2">
              <div className="flex flex-wrap gap-2">
                {user.role === "recruiter" && !cand.assigned_recruiter_id && !cand.blacklisted && (
                  <Button onClick={openClaim} disabled={busy} className="rounded-lg" data-testid="candidate-claim-button">
                    <Play className="h-4 w-4 mr-1.5" /> Start Working
                  </Button>
                )}
                {(isOwner || isAdmin) && cand.assigned_recruiter_id && (
                  <Button variant="outline" onClick={() => setCloseOpen(true)} className="rounded-lg" data-testid="candidate-close-lead-button">
                    <CheckCircle2 className="h-4 w-4 mr-1.5" /> Close Lead
                  </Button>
                )}
                {isAdmin && cand.assigned_recruiter_id && (
                  <Button variant="ghost" onClick={async () => { await api.post(`/candidates/${candidateId}/release`); toast.success("Ownership released"); load(); }} className="rounded-lg text-muted-foreground" data-testid="candidate-release-button">
                    Release
                  </Button>
                )}
                {cand.can_edit && !editing && (
                  <Button variant="outline" onClick={() => setEditing(true)} className="rounded-lg" data-testid="candidate-edit-button">
                    <Pencil className="h-4 w-4 mr-1.5" /> Edit Profile
                  </Button>
                )}
                {editing && (
                  <>
                    <Button onClick={save} disabled={busy} className="rounded-lg" data-testid="candidate-save-button">
                      <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving..." : "Save Changes"}
                    </Button>
                    <Button variant="ghost" onClick={() => { setForm(cand); setEditing(false); }} className="rounded-lg" data-testid="candidate-cancel-edit">Cancel</Button>
                  </>
                )}
              </div>
              <div className="w-full sm:w-56">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Profile completion</span>
                  <span className="font-medium text-foreground" data-testid="candidate-completion-percent">{cand.profile_completion}%</span>
                </div>
                <Progress value={cand.profile_completion} className="h-1.5" />
              </div>
            </div>
          </div>
          {ownedByOther && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs" data-testid="candidate-locked-banner">
              <Lock className="h-3.5 w-3.5 shrink-0" />
              This candidate is being worked on by {cand.owner_name}. You can view the profile and journey, but editing and contacting are locked until the lead is closed.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-5 items-start">
        {/* Left: sections */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="rounded-xl border-border shadow-none">
            <CardContent className="p-2 sm:p-4">
              <Accordion type="multiple" defaultValue={["personal", "professional"]} className="w-full">
                <CandidateProfileFields cand={cand} form={form} set={set} master={master} canEdit={canEdit} />

                {/* System info */}
                <AccordionItem value="system">
                  <AccordionTrigger className="text-sm font-semibold px-2" data-testid="section-system">System Information</AccordionTrigger>
                  <AccordionContent className="px-2">
                    <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <p className="text-muted-foreground">Candidate ID: <span className="font-mono text-foreground">{cand.code}</span></p>
                      <p className="text-muted-foreground">Status: <span className="text-foreground">{cand.candidate_status}</span></p>
                      <p className="text-muted-foreground">Created: <span className="text-foreground">{formatDateTime(cand.created_at)}</span></p>
                      <p className="text-muted-foreground">Updated: <span className="text-foreground">{formatDateTime(cand.updated_at)}</span></p>
                      <p className="text-muted-foreground">Created by: <span className="text-foreground">{cand.created_by?.name || "System"}</span></p>
                      <p className="text-muted-foreground">Updated by: <span className="text-foreground">{cand.updated_by?.name || "—"}</span></p>
                      <p className="text-muted-foreground">Source: <span className="text-foreground">{cand.source || "—"}</span></p>
                      <p className="text-muted-foreground">Duplicate status: <span className="text-foreground">{cand.duplicate_status}</span></p>
                      <p className="text-muted-foreground">Verification: <span className="text-foreground">{cand.verification_status}</span></p>
                      <p className="text-muted-foreground">Last activity: <span className="text-foreground">{formatDateTime(cand.last_activity_at)}</span></p>
                      <p className="text-muted-foreground">Last resume update: <span className="text-foreground">{cand.last_resume_update ? formatDateTime(cand.last_resume_update) : "—"}</span></p>
                      <p className="text-muted-foreground">Last contact: <span className="text-foreground">{cand.last_contact_date ? formatDateTime(cand.last_contact_date) : "—"}</span></p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          {/* Internal notes */}
          <Card className="rounded-xl border-border shadow-none" data-testid="candidate-notes-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-primary" /> Internal Notes <span className="text-[10px] font-normal text-muted-foreground">(staff only — permanent)</span></CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Recruiter notes">
                <Textarea data-testid="notes-recruiter" rows={2} value={form.recruiter_notes || ""} disabled={!cand.can_edit} onChange={(e) => set("recruiter_notes", e.target.value)} onBlur={() => cand.can_edit && form.recruiter_notes !== cand.recruiter_notes && saveNotes({ recruiter_notes: form.recruiter_notes })} placeholder="Notes from screening calls..." />
              </Field>
              {isAdmin && (
                <Field label="Admin notes">
                  <Textarea data-testid="notes-admin" rows={2} value={form.admin_notes || ""} onChange={(e) => set("admin_notes", e.target.value)} onBlur={() => form.admin_notes !== cand.admin_notes && saveNotes({ admin_notes: form.admin_notes })} placeholder="Admin-only notes..." />
                </Field>
              )}
              <Field label="Important remarks">
                <Textarea data-testid="notes-remarks" rows={2} value={form.important_remarks || ""} disabled={!cand.can_edit} onChange={(e) => set("important_remarks", e.target.value)} onBlur={() => cand.can_edit && form.important_remarks !== cand.important_remarks && saveNotes({ important_remarks: form.important_remarks })} placeholder="Anything the next recruiter must know..." />
              </Field>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch data-testid="notes-dnc-switch" checked={!!cand.do_not_contact} disabled={!cand.can_edit} onCheckedChange={(v) => saveNotes({ do_not_contact: v })} />
                  Do Not Contact
                </label>
                <label className="flex items-center gap-2 text-sm font-medium">
                  <Switch data-testid="notes-blacklist-switch" checked={!!cand.blacklisted} disabled={!isAdmin} onCheckedChange={(v) => saveNotes({ blacklisted: v })} />
                  Blacklisted {!isAdmin && <span className="text-[10px] text-muted-foreground">(admin only)</span>}
                </label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right rail */}
        <div className="space-y-5">
          {/* Resume versions */}
          <Card className="rounded-xl border-border shadow-none" data-testid="candidate-resumes-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Resume ({resumes.length} version{resumes.length === 1 ? "" : "s"})</CardTitle>
              <p className="text-xs text-muted-foreground">PDF, DOC or DOCX · max 5 MB. Latest upload becomes Active; older versions stay downloadable.</p>
            </CardHeader>
            <CardContent className="space-y-2.5">
              {cand.can_edit && (
                <>
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadResume(e.target.files?.[0])} data-testid="resume-file-input" />
                  <Button variant="outline" className="w-full rounded-lg" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="resume-upload-button">
                    <Upload className="h-4 w-4 mr-1.5" /> Upload resume
                  </Button>
                </>
              )}
              {resumes.length === 0 && <p className="text-sm text-muted-foreground">No resume uploaded yet.</p>}
              {resumes.map((r) => (
                <div key={r.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid="resume-version-item">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{r.filename}</p>
                    <p className="text-[10px] text-muted-foreground">v{r.version} · {(r.size / 1024).toFixed(0)} KB · {formatDate(r.uploaded_at)} · by {r.uploaded_by?.name}</p>
                  </div>
                  {r.active && <Badge className="rounded-full text-[9px] bg-emerald-600 text-white shrink-0">Active</Badge>}
                  <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => downloadResume(r)} aria-label="Download resume" data-testid="resume-download-button">
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Journey */}
          <Card className="rounded-xl border-border shadow-none" data-testid="candidate-journey-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Journey History ({journey.length})</CardTitle>
              <p className="text-xs text-muted-foreground">Complete permanent history — past records can never be modified.</p>
            </CardHeader>
            <CardContent>
              <div className="relative pl-5 space-y-4 max-h-[420px] overflow-y-auto">
                <span className="absolute left-[5px] top-2 bottom-2 w-px bg-border" />
                {journey.length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
                {journey.map((j) => (
                  <div key={j.id} className="relative" data-testid="journey-item">
                    <span className="absolute -left-[19px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                    <p className="text-xs font-medium">{j.action.replaceAll("_", " ")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{j.details}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{j.actor_name} · {formatDateTime(j.at)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Close lead dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent data-testid="close-lead-dialog">
          <DialogHeader><DialogTitle>Close this lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Closing releases ownership so another recruiter can work on this candidate later. The full journey stays on the profile.</p>
            <Field label="Final disposition *">
              <Select value={disposition || ANY} onValueChange={(v) => setDisposition(v === ANY ? "" : v)}>
                <SelectTrigger data-testid="close-lead-disposition"><SelectValue placeholder="Select disposition" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Select disposition</SelectItem>
                  {(master.disposition || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Note (optional)">
              <Textarea data-testid="close-lead-note" rows={2} value={closeNote} onChange={(e) => setCloseNote(e.target.value)} placeholder="Summary of the final conversation..." />
            </Field>
            <Button onClick={closeLead} disabled={busy} className="w-full rounded-lg" data-testid="close-lead-confirm">
              {busy ? "Closing..." : "Close Lead"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M17: Directory claim → fresh application dialog */}
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="claim-to-app-dialog">
          <DialogHeader><DialogTitle className="font-heading">Start working on {cand.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A fresh application is created for this candidate with a new application ID — their complete
              history stays on the profile. The candidate's mobile number unlocks once you claim. You'll pick
              the target company later, when you schedule the interview.
            </p>
            <Button onClick={submitClaim} disabled={busy} className="w-full rounded-lg" data-testid="claim-to-app-submit">
              <Play className="h-4 w-4 mr-1.5" /> {busy ? "Claiming..." : "Claim & create application"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M17: one-undisposed-candidate blocking dialog */}
      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Finish your current candidate first</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="conflict-message">{conflict?.message}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button className="rounded-lg flex-1" data-testid="conflict-goto-btn"
                    onClick={() => { const id = conflict?.active_application_id; setConflict(null); navigate(`/recruiter/applications/${id}`); }}>
              Open {conflict?.candidate_name} ({conflict?.active_app_code})
            </Button>
            <Button variant="outline" className="rounded-lg bg-white" data-testid="conflict-close-btn" onClick={() => setConflict(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
